'use client'

import React, { useState, useRef, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Bot, User, Send, Minimize2, Maximize2, MessageCircle, Loader } from 'lucide-react'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'

interface Message {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: Date
  isLoading?: boolean
}

interface SomaAIChatProps {
  wardId?: string
  className?: string
}

export function SomaAIChat({ wardId = '3A', className }: SomaAIChatProps) {
  const [messages, setMessages] = useState<Message[]>([])

  // Initialize welcome message when wardId changes
  useEffect(() => {
    setMessages([{
      id: '1',
      type: 'assistant',
      content: `안녕하세요! 저는 SOMA AI입니다. ${wardId} 병동의 환자 상태와 위험도 분석에 대해 도움을 드릴 수 있습니다. 무엇을 도와드릴까요?`,
      timestamp: new Date()
    }])
  }, [wardId])
  const [input, setInput] = useState('')
  const [isMinimized, setIsMinimized] = useState(true) // Start minimized
  const [isHovered, setIsHovered] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    }

    const loadingMessage: Message = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true
    }

    setMessages(prev => [...prev, userMessage, loadingMessage])
    setInput('')
    setIsLoading(true)

    try {
      // Call the RAG API
      const response = await fetch('/api/v1/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: input.trim(),
          type: 'patient_risk'
        })
      })

      if (!response.ok) {
        throw new Error('API 요청 실패')
      }

      const data = await response.json()
      const aiResponse = data.answer || '응답을 생성할 수 없습니다.'

      setMessages(prev => prev.map(msg =>
        msg.isLoading ? { ...msg, content: aiResponse, isLoading: false } : msg
      ))
    } catch (error) {
      console.error('AI response error:', error)
      setMessages(prev => prev.map(msg =>
        msg.isLoading ? {
          ...msg,
          content: '죄송합니다. 일시적인 오류가 발생했습니다. 다시 시도해 주세요.',
          isLoading: false
        } : msg
      ))
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const generateAIResponse = (query: string, ward: string): string => {
    const lowerQuery = query.toLowerCase()

    if (lowerQuery.includes('위험도') || lowerQuery.includes('risk')) {
      return `${ward} 병동의 현재 위험도 현황을 분석해드릴게요. 고위험 환자들의 주요 위험 요인은 다음과 같습니다:\n\n• 호흡수 감소 (RR < 10/min)\n• 산소포화도 저하 (SpO2 < 90%)\n• 진정제 누적용량 증가\n• GCS 점수 감소\n\n즉시 의료진의 주의가 필요한 환자가 있는지 확인해 보시기 바랍니다.`
    }

    if (lowerQuery.includes('환자') || lowerQuery.includes('patient')) {
      return `${ward} 병동의 환자 현황을 확인해드릴게요. 현재 모니터링 중인 환자들의 상태를 실시간으로 추적하고 있으며, 위험도 변화가 감지되면 즉시 알려드립니다. 특정 환자에 대해 더 자세한 정보가 필요하시면 환자명이나 병실 번호를 알려주세요.`
    }

    if (lowerQuery.includes('경보') || lowerQuery.includes('알림') || lowerQuery.includes('alert')) {
      return `경보 시스템 현황을 안내해드릴게요. 현재 활성화된 경보들은 위험도 75% 이상인 환자들에게 발생하고 있습니다. 경보 유형:\n\n🔴 긴급: 즉시 조치 필요 (위험도 85% 이상)\n🟡 주의: 모니터링 강화 (위험도 75-85%)\n🔵 정보: 추가 관찰 필요 (위험도 50-75%)\n\n경보 설정을 변경하려면 우측 상단의 설정 버튼을 이용해 주세요.`
    }

    if (lowerQuery.includes('shap') || lowerQuery.includes('분석')) {
      return `SHAP 분석 결과를 통해 각 환자의 위험도에 기여하는 주요 임상 변수들을 확인할 수 있습니다:\n\n• 호흡 관련: 호흡수, SpO2, 산소 공급량\n• 순환계: 혈압, 심박수, 체온\n• 의식 수준: GCS 점수, RASS 점수\n• 약물 영향: 진정제, 진통제 투여량\n\n각 환자의 상세 페이지에서 개별 SHAP 분석 결과를 확인하실 수 있습니다.`
    }

    if (lowerQuery.includes('도움') || lowerQuery.includes('help') || lowerQuery.includes('기능')) {
      return `SOMA AI가 제공하는 주요 기능들입니다:\n\n📊 실시간 위험도 분석 및 모니터링\n🚨 지능형 경보 시스템\n📈 환자별 SHAP 분석 리포트\n👥 병동별 환자 현황 요약\n📋 의료진 조치 가이드라인\n📞 응급 상황 대응 프로토콜\n\n구체적인 질문이나 도움이 필요한 항목이 있으시면 언제든 말씀해 주세요!`
    }

    // Default response
    return `${ward} 병동 관련 문의사항에 대해 도움을 드리겠습니다. 다음과 같은 질문을 하실 수 있습니다:\n\n• "현재 위험도가 높은 환자는 누구인가요?"\n• "경보 현황을 알려주세요"\n• "SHAP 분석 결과가 무엇을 의미하나요?"\n• "환자별 상세 정보를 확인하고 싶어요"\n\n더 구체적인 질문을 해주시면 정확한 답변을 드릴 수 있습니다.`
  }

  if (isMinimized) {
    return (
      <div className={cn("fixed bottom-4 right-4 z-50", className)}>
        <div
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Button
            onClick={() => setIsMinimized(false)}
            className="h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200"
            size="icon"
          >
            <MessageCircle className="h-5 w-5" />
          </Button>

          {isHovered && (
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg whitespace-nowrap animate-in slide-in-from-bottom-1 duration-200">
              SOMA AI와 채팅해보세요!
              <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Card className={cn("fixed bottom-4 right-4 w-96 h-[500px] z-50 shadow-xl", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-full">
              <Bot className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">SOMA AI</CardTitle>
              <Badge variant="secondary" className="text-xs">
                {wardId} 병동 전담
              </Badge>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMinimized(true)}
            className="h-8 w-8"
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0 flex flex-col h-[calc(500px-80px)]">
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-4 pb-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex gap-3",
                  message.type === 'user' ? "justify-end" : "justify-start"
                )}
              >
                {message.type === 'assistant' && (
                  <div className="p-2 bg-primary/10 rounded-full self-start mt-1">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[80%] rounded-lg px-3 py-2 text-sm",
                    message.type === 'user'
                      ? "bg-primary text-primary-foreground ml-12"
                      : "bg-muted"
                  )}
                >
                  {message.isLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>응답을 생성하는 중...</span>
                    </div>
                  ) : message.type === 'assistant' ? (
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown
                        components={{
                          h1: ({children}) => <h1 className="text-base font-semibold mb-2 text-foreground">{children}</h1>,
                          h2: ({children}) => <h2 className="text-sm font-semibold mb-2 text-foreground">{children}</h2>,
                          h3: ({children}) => <h3 className="text-sm font-medium mb-1 text-foreground">{children}</h3>,
                          h4: ({children}) => <h4 className="text-sm font-medium mb-1 text-foreground">{children}</h4>,
                          p: ({children}) => <p className="text-sm leading-relaxed mb-2 text-foreground">{children}</p>,
                          ul: ({children}) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                          ol: ({children}) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                          li: ({children}) => <li className="text-sm text-foreground">{children}</li>,
                          strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
                          code: ({children}) => <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                          blockquote: ({children}) => <blockquote className="border-l-4 border-primary/30 pl-4 italic text-muted-foreground">{children}</blockquote>
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-sm leading-relaxed">{message.content}</div>
                  )}
                  <div className={cn(
                    "text-xs opacity-70 mt-1",
                    message.type === 'user' ? "text-primary-foreground/70" : "text-muted-foreground"
                  )}>
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                {message.type === 'user' && (
                  <div className="p-2 bg-primary rounded-full self-start mt-1">
                    <User className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </div>
            ))}
            <div ref={scrollRef} />
          </div>
        </ScrollArea>

        <div className="border-t p-4">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="SOMA AI에게 질문해보세요..."
              className="flex-1"
              disabled={isLoading}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              size="icon"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}